import {Component, DoCheck} from '@angular/core';
import {TreeNode} from 'primeng/api';
import {MeterItem} from 'primeng/metergroup';
import {Skeleton} from 'primeng/skeleton';
import {
    ECpmCTaskAttributes1ApiEnum,
    ECpmCTaskStatusApiEnum,
    ECpmCTaskTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-task.api-enums';
import {IDataCellDisplayComponent} from '../../../../sdk/src/data-cell-display/idata-cell-display/idata-cell-display.component';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {SignatureUtils} from '../../../../sdk/src/utils/signature.utils';
import {StringUtils} from '../../../../sdk/src/utils/string.util';
import {TaskTreeViewExtension} from '../../api-extensions/views/task-tree.view-extension';
import {TaskUtils} from '../../utils/task.utils';

/**
 * Task status data cell display component
 */
@Component({
    templateUrl: './task-status-data-cell-display.component.html',
    imports: [Skeleton, PrimeNgImportsModule]
})
export class TaskStatusDataCellDisplayComponent
    extends IDataCellDisplayComponent<TaskTreeViewExtension, unknown>
    implements DoCheck
{
    protected readonly TaskUtils = TaskUtils;
    protected readonly ECpmCTaskTypeApiEnum = ECpmCTaskTypeApiEnum;

    // ===============================================================
    // ===============================================================

    protected _task?: TaskTreeViewExtension;
    protected _groupDetail?: MeterItem[];
    protected _groupTooltip?: string;
    protected _groupCompletionPct?: number;
    protected _isWaitingForInput = false;
    protected _stateSignature?: number;
    protected _isOutdated = false;
    protected _isOutdatedFailed = false;

    // ===============================================================
    // ===============================================================

    /**
     * Do check: task / sub-task statuses may be mutated in place (e.g. by Oo8 events) without the row
     * reference changing, so detect it here and recompute the derived display state
     */
    public ngDoCheck(): void {
        if (this.displayType !== 'tree' || !this.row || this._stateSignature === undefined) return;

        if (this.computeStateSignature(this.row as TreeNode<TaskTreeViewExtension>) !== this._stateSignature)
            this.computeState(this.row as TreeNode<TaskTreeViewExtension>);
    }

    /**
     * On refresh (Error handling and refresh is done in the parent)
     */
    protected override async onRefresh(): Promise<void> {
        if (this.displayType !== 'tree')
            throw new Error('Only tree is supported for TaskStatusDataCellDisplayComponent');

        this.computeState(this.row as TreeNode<TaskTreeViewExtension>);
    }

    // ===============================================================
    // ===============================================================

    /**
     * Build a cheap signature of everything the derived display state depends on. Runs on every change
     * detection pass, so it must stay allocation free
     * @param taskNode Task tree node
     */
    private computeStateSignature(taskNode: TreeNode<TaskTreeViewExtension>): number {
        const task = taskNode.data;
        if (!task) return SignatureUtils.START;

        let signature = SignatureUtils.mixToken(SignatureUtils.START, task.task_type);
        signature = SignatureUtils.mixToken(signature, task.status);
        signature = SignatureUtils.mixNumber(signature, task.attributes1);

        return SignatureUtils.mixTokens(signature, taskNode.children, (x) => x.data?.status);
    }

    /**
     * Compute derived display state (group meter / waiting for input)
     * @param taskNode Task tree node
     */
    private computeState(taskNode: TreeNode<TaskTreeViewExtension>): void {
        this._stateSignature = this.computeStateSignature(taskNode);
        this._task = taskNode.data;
        this._isWaitingForInput = false;
        this._isOutdated = !!this._task && TaskUtils.isOutdated(this._task);
        this._isOutdatedFailed = !!this._task && TaskUtils.isOutdatedFailed(this._task);

        if (this._task?.task_type === ECpmCTaskTypeApiEnum.GROUP) {
            const countPerStatus = Object.fromEntries(Object.keys(ECpmCTaskStatusApiEnum).map((x) => [x, 0]));
            const childCount = taskNode.children?.length ?? 1;

            taskNode.children?.forEach((subTask) => {
                countPerStatus[subTask.data!.status]++;
            });

            this._groupDetail = Object.keys(countPerStatus)
                .map((k) => ({
                    color: TaskUtils.statusTagsByStatus[k as ECpmCTaskStatusApiEnum].iconStyle!['color'],
                    value: childCount ? (countPerStatus[k] / childCount) * 100 : 0
                }))
                .reverse();

            this._groupTooltip = Object.keys(countPerStatus)
                .filter((k) => countPerStatus[k] > 0)
                .map((k) => `${StringUtils.toHumanFormatedStr(k)}: ${countPerStatus[k]}`)
                .join('\n');

            this._groupCompletionPct =
                childCount > 0 ? Math.round((countPerStatus[ECpmCTaskStatusApiEnum.SUCCESS] / childCount) * 100) : 0;
        } else if (
            this._task &&
            (this._task.attributes1 & ECpmCTaskAttributes1ApiEnum.SMART_REQUIRES_MANUAL_INPUT) !== 0
        ) {
            switch (this._task.status) {
                case ECpmCTaskStatusApiEnum.READY:
                    this._isWaitingForInput = true;
                    break;

                default:
                    this._isWaitingForInput = false;
                    break;
            }
        }
    }
}
